import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Plus, Pause, Play, XCircle, BarChart3, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";
import CampaignEditor from "./CampaignEditor";

const STATUS_COLORS = {
  draft: "bg-slate-500/15 text-slate-400", scheduled: "bg-blue-500/15 text-blue-400",
  sending: "bg-emerald-500/15 text-emerald-500", paused: "bg-amber-500/15 text-amber-500",
  completed: "bg-emerald-600/15 text-emerald-400", cancelled: "bg-red-500/15 text-red-400",
  failed: "bg-red-600/20 text-red-500",
};

export default function CampaignsTab({ meta, onChanged }) {
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null); // campaign object or "new"
  const [stats, setStats] = useState(null);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${MKT}/campaigns`, getAdminAuth());
      setCampaigns(res.data.campaigns);
    } catch (e) { toast.error("Failed to load campaigns"); }
  };
  useEffect(() => { fetchCampaigns(); }, []);

  const action = async (c, verb) => {
    try {
      await axios.post(`${MKT}/campaigns/${c.campaign_id}/${verb}`, {}, getAdminAuth());
      toast.success(`Campaign ${verb}d`);
      fetchCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await axios.delete(`${MKT}/campaigns/${c.campaign_id}`, getAdminAuth());
      fetchCampaigns(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const viewStats = async (c) => {
    try {
      const res = await axios.get(`${MKT}/campaigns/${c.campaign_id}/stats`, getAdminAuth());
      setStats(res.data);
    } catch (e) { toast.error("Failed to load stats"); }
  };

  const progress = (c) => {
    const rc = c.recipient_counts || {};
    const done = (rc.sent || 0) + (rc.failed || 0) + (rc.skipped_suppressed || 0) + (rc.cancelled || 0);
    const total = Object.values(rc).reduce((a, b) => a + b, 0);
    return total ? Math.round(done / total * 100) : 0;
  };

  if (editing) {
    return <CampaignEditor campaign={editing === "new" ? null : editing}
      onClose={() => { setEditing(null); fetchCampaigns(); onChanged?.(); }} />;
  }

  return (
    <div className="space-y-4 mt-4" data-testid="marketing-campaigns-tab">
      <div className="flex justify-between items-center">
        <p className="text-xs text-muted-foreground">Campaigns send in rate-limited batches with duplicate-send protection and a final suppression check before every email.</p>
        <Button size="sm" onClick={() => setEditing("new")} data-testid="new-campaign-btn"><Plus className="w-4 h-4 mr-1" />New Campaign</Button>
      </div>
      <div className="space-y-2">
        {campaigns.map(c => (
          <Card key={c.campaign_id} className="border-border" data-testid={`campaign-card-${c.name}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground truncate">{c.name}</p>
                    <Badge className={`text-[10px] ${STATUS_COLORS[c.status] || ""}`} data-testid={`campaign-status-${c.name}`}>{c.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.subject || "no subject yet"}</p>
                  {c.status !== "draft" && (
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="w-40 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${progress(c)}%` }} />
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {(c.recipient_counts?.sent || 0)} sent · {(c.recipient_counts?.queued || 0)} queued
                        {c.recipient_counts?.skipped_suppressed ? ` · ${c.recipient_counts.skipped_suppressed} suppressed` : ""}
                        {c.recipient_counts?.failed ? ` · ${c.recipient_counts.failed} failed` : ""}
                      </span>
                    </div>
                  )}
                  {c.auto_paused && c.status === "paused" && (
                    <p className="text-[10px] text-amber-500 mt-1 flex items-start gap-1" data-testid={`auto-pause-reason-${c.name}`}>
                      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{c.pause_reason}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {c.status !== "draft" && <Button variant="ghost" size="sm" onClick={() => viewStats(c)} data-testid={`stats-${c.name}`}><BarChart3 className="w-4 h-4" /></Button>}
                  {["draft", "scheduled"].includes(c.status) && <Button variant="ghost" size="sm" onClick={() => setEditing(c)} data-testid={`edit-${c.name}`}><Pencil className="w-4 h-4" /></Button>}
                  {c.status === "sending" && <Button variant="ghost" size="sm" onClick={() => action(c, "pause")} data-testid={`pause-${c.name}`}><Pause className="w-4 h-4 text-amber-400" /></Button>}
                  {c.status === "paused" && <Button variant="ghost" size="sm" onClick={() => action(c, "resume")} data-testid={`resume-${c.name}`}><Play className="w-4 h-4 text-emerald-400" /></Button>}
                  {["sending", "paused", "scheduled"].includes(c.status) && <Button variant="ghost" size="sm" onClick={() => action(c, "cancel")} data-testid={`cancel-${c.name}`}><XCircle className="w-4 h-4 text-red-400" /></Button>}
                  {["draft", "completed", "cancelled"].includes(c.status) && <Button variant="ghost" size="sm" onClick={() => remove(c)}><Trash2 className="w-4 h-4 text-red-400" /></Button>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {campaigns.length === 0 && <p className="text-sm text-muted-foreground">No campaigns yet.</p>}
      </div>

      {/* Stats dialog */}
      <Dialog open={!!stats} onOpenChange={() => setStats(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="campaign-stats-dialog">
          <DialogHeader><DialogTitle className="text-sm">{stats?.campaign?.name} — Analytics</DialogTitle></DialogHeader>
          {stats && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Sent", stats.sent], ["Delivered", stats.delivered], ["Failed", stats.failed],
                  ["Unique opens", stats.unique_opens], ["Unique clicks", stats.unique_clicks],
                  ["Unsubscribed", stats.unsubscribed], ["Hard bounce", stats.hard_bounced],
                  ["Soft bounce", stats.soft_bounced], ["Complaints", stats.complaints]].map(([l, v]) => (
                  <div key={l} className="p-2 rounded bg-muted/40">
                    <p className="text-lg font-bold text-foreground tabular-nums">{v}</p>
                    <p className="text-[10px] text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(stats.rates || {}).map(([k, v]) => (
                  <div key={k} className="p-2 rounded border border-border">
                    <p className="text-sm font-semibold text-foreground">{v}%</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{k} rate</p>
                  </div>
                ))}
              </div>
              {stats.campaign?.eligibility_summary && (
                <p className="text-[11px] text-muted-foreground">
                  Targeted {stats.campaign.eligibility_summary.targeted} → eligible {stats.campaign.eligibility_summary.eligible}
                  {" "}(excluded: {stats.campaign.eligibility_summary.unsubscribed} unsub, {stats.campaign.eligibility_summary.hard_bounced} bounced, {stats.campaign.eligibility_summary.complaints} complaints, {stats.campaign.eligibility_summary.invalid} invalid, {stats.campaign.eligibility_summary.duplicates} dupes)
                </p>
              )}
              <p className="text-[10px] text-muted-foreground">{stats.note}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
